<template>
    <section class="create-shipment">
        <div class="create-shipment__header">
            <SubHeader
                :title="$t('pages.shipments.createShipment')"
                :crumbs="textContent['crumbs']"
            />
            <button class="create-shipment__back-btn" @click="goBack">
                <v-icon class="create-shipment__back-icon"
                    >mdi-chevron-left</v-icon
                >
                {{ $t('pages.shipments.returnToShipments') }}
            </button>
        </div>

        <div class="create-shipment__body">
            <div class="create-shipment__content">
                <ShipmentForm
                    :selected-senders="selectedSenders"
                    :users-items="usersItems"
                    :is-loading-users="isLoadingUsers"
                    :selected-items="selectedItems"
                    :is-submitting="isSubmitting"
                    :recipients-items="recipientsItems"
                    :is-loading-recipients="isLoadingRecipients"
                    :selected-recipient-id="selectedRecipientId"
                    :recipient-first-name="recipientFirstName"
                    :recipient-last-name="recipientLastName"
                    @update:selectedSenders="selectedSenders = $event"
                    @senders-change="onSendersChange"
                    @update-item-price="updateItemPrice"
                    @remove-item="removeItem"
                    @update:selectedRecipientId="onRecipientChange"
                    @update:recipientFirstName="recipientFirstName = $event"
                    @update:recipientLastName="recipientLastName = $event"
                    @cancel="goBack"
                    @submit="submitForm"
                />

                <ShipmentsWigsList
                    :wigs="availableWigs"
                    :managers-with-wigs="orderedManagersWithWigs"
                    :selected-wigs="selectedWigsIds"
                    :is-loading="isLoadingWigs"
                    :has-manager="hasSelectedSenders"
                    @update:selectedWigs="onWigsSelectionChange"
                    @refresh-wigs="onWigsRefresh"
                    @currency-validation-error="onCurrencyValidationError"
                />
            </div>
        </div>
    </section>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import SubHeader from '@/components/common/sub-header'
import ShipmentsWigsList from '@/components/shipments/shipments-wigs-list'
import ShipmentForm from '@/components/shipments/shipment-form'
import { mapActions, mapGetters } from 'vuex'
import {
    actionTypes as shipmentsActionTypes,
    getterTypes as shipmentsGetterTypes,
} from '@/store/modules/shipments'
import { actionTypes as modalNotificationActionTypes } from '@/store/modules/modal-notifications'

export default {
    name: 'CreateShipment',
    components: {
        SubHeader,
        ShipmentsWigsList,
        ShipmentForm,
    },
    mixins: [languageMixin],
    data: () => ({
        isSubmitting: false,
        selectedSenders: [null],
        selectedWigsIds: [],
        selectedItems: [],
        selectedRecipientId: null,
        recipientFirstName: '',
        recipientLastName: '',
    }),
    computed: {
        ...mapGetters('shipments', {
            shipmentUsers: shipmentsGetterTypes.getShipmentUsers,
            shipmentUserWigs: shipmentsGetterTypes.getShipmentUserWigs,
            shipmentManagersWithWigs:
                shipmentsGetterTypes.getShipmentManagersWithWigs,
            shipmentRecipients: shipmentsGetterTypes.getShipmentRecipients,
            isLoadingUsers: shipmentsGetterTypes.isLoadingUsers,
            isLoadingWigs: shipmentsGetterTypes.isLoadingWigs,
            isLoadingRecipients: shipmentsGetterTypes.isLoadingRecipients,
        }),
        usersItems: function () {
            return this.shipmentUsers.map((user) => ({
                text: user.nickName,
                value: user.id,
            }))
        },
        recipientsItems: function () {
            return this.shipmentRecipients.map((recipient) => {
                return {
                    text: recipient.nickName,
                    value: recipient.id,
                }
            })
        },
        availableWigs: function () {
            return this.shipmentUserWigs || []
        },
        orderedManagersWithWigs: function () {
            const managersById = new Map(
                this.shipmentManagersWithWigs.map((manager) => [
                    manager.id,
                    manager,
                ])
            )
            const ordered = []
            const seen = new Set()

            this.selectedSenders.forEach((senderId) => {
                if (senderId == null || seen.has(senderId)) return
                const manager = managersById.get(senderId)
                if (manager) {
                    ordered.push(manager)
                    seen.add(senderId)
                }
            })

            this.shipmentManagersWithWigs.forEach((manager) => {
                if (!seen.has(manager.id)) {
                    ordered.push(manager)
                }
            })

            return ordered
        },
        hasSelectedSenders: function () {
            return (
                Array.isArray(this.selectedSenders) &&
                this.selectedSenders.length > 0 &&
                this.selectedSenders.some(
                    (sender) =>
                        sender !== null && sender !== '' && sender !== undefined
                )
            )
        },
    },
    watch: {
        availableWigs: {
            handler: function () {
                if (this.selectedItems.length > 0) {
                    this.syncSelectedItemsWithUpdatedWigs()
                }
            },
            deep: true,
        },
        shipmentManagersWithWigs: {
            handler: function () {
                if (this.selectedItems.length > 0) {
                    this.syncSelectedItemsWithUpdatedWigs()
                }
            },
            deep: true,
        },
        shipmentRecipients: function (recipients) {
            if (this.selectedRecipientId == null) {
                return
            }
            const stillAvailable = (recipients || []).some(
                (r) => r.id === this.selectedRecipientId
            )
            if (!stillAvailable) {
                this.selectedRecipientId = null
            }
        },
    },
    mounted() {
        this.resetShipmentManagersWithWigs()
        this.resetShipmentRecipients()
        this.loadUsers()
        this.loadRecipients()
    },
    methods: {
        ...mapActions('shipments', {
            fetchShipmentUsers: shipmentsActionTypes.fetchShipmentUsers,
            fetchShipmentUserWigs: shipmentsActionTypes.fetchShipmentUserWigs,
            fetchShipmentUsersWithWigs:
                shipmentsActionTypes.fetchShipmentUsersWithWigs,
            resetShipmentManagersWithWigs:
                shipmentsActionTypes.resetShipmentManagersWithWigs,
            fetchShipmentRecipients:
                shipmentsActionTypes.fetchShipmentRecipients,
            resetShipmentRecipients:
                shipmentsActionTypes.resetShipmentRecipients,
            createShipment: shipmentsActionTypes.createShipment,
        }),
        ...mapActions('modalNotifications', {
            showError: modalNotificationActionTypes.error,
            showWarning: modalNotificationActionTypes.warning,
        }),
        isItemRepair: function (item) {
            return (
                item?.wig?.is_repair === true || item?.wig?.is_repair === 1
            )
        },
        isPriceEmpty: function (price) {
            return price === null || price === undefined || price === ''
        },
        isPriceNegative: function (price) {
            return !this.isPriceEmpty(price) && Number(price) < 0
        },
        getPriceValidationError: function (items) {
            const hasInvalidNewWig = items.some(
                (item) =>
                    !this.isItemRepair(item) &&
                    (!item.price || item.price <= 0)
            )
            if (hasInvalidNewWig) {
                return this.$t('pages.shipments.allWigsMustHavePrice')
            }

            const hasNegativeRepair = items.some(
                (item) =>
                    this.isItemRepair(item) && this.isPriceNegative(item.price)
            )
            if (hasNegativeRepair) {
                return this.$t('pages.shipments.priceMustBePositive')
            }

            return ''
        },
        resolveItemPrice: function (item) {
            return this.isItemRepair(item) && this.isPriceEmpty(item.price)
                ? 0
                : item.price
        },
        loadUsers: async function () {
            try {
                await this.fetchShipmentUsers()
            } catch (error) {
                console.error('Error loading users:', error)
                this.showError(
                    error?.response?.data?.message ||
                        this.$t('pages.shipments.errorLoadingUsers')
                )
            }
        },
        loadRecipients: async function () {
            try {
                await this.fetchShipmentRecipients()
            } catch (error) {
                console.error('Error loading recipients:', error)
                this.showError(
                    error?.response?.data?.message ||
                        this.$t('pages.shipments.errorLoadingRecipients')
                )
            }
        },
        onSendersChange: async function () {
            const validSenders = this.selectedSenders.filter(
                (sender) =>
                    sender !== null && sender !== '' && sender !== undefined
            )
            const validSenderIds = new Set(validSenders)

            this.selectedItems = this.selectedItems.filter((item) =>
                validSenderIds.has(item.wig?.managerId)
            )
            this.selectedWigsIds = this.selectedWigsIds.filter((wig) =>
                validSenderIds.has(wig.managerId)
            )

            if (!this.hasSelectedSenders) {
                this.selectedRecipientId = null
                this.recipientFirstName = ''
                this.recipientLastName = ''
                this.resetShipmentManagersWithWigs()
                this.resetShipmentRecipients()
                return
            }

            try {
                await Promise.all([
                    this.fetchShipmentRecipients(),
                    this.fetchShipmentUsersWithWigs(validSenders),
                ])
            } catch (error) {
                console.error('Error loading data:', error)
                this.showError(
                    error?.response?.data?.message ||
                        this.$t('pages.shipments.errorLoadingWigs')
                )
            }
        },
        updateSelectedItems: function (wigs) {
            const existingCurrencies = this.selectedItems
                .map((item) => item.wig?.price_currency || 'ILS')
                .filter(
                    (currency, index, self) => self.indexOf(currency) === index
                )

            const validWigs = wigs.filter((wig) => {
                const wigCurrency = wig.price_currency || 'ILS'
                return (
                    existingCurrencies.length === 0 ||
                    existingCurrencies.includes(wigCurrency)
                )
            })

            this.selectedItems = validWigs.map((wig) => {
                const existing = this.selectedItems.find(
                    (item) => item.wig.id === wig.id
                )
                if (existing) {
                    return existing
                }
                return {
                    wig,
                    price:
                        wig.price != null && wig.price !== 0 ? wig.price : null,
                }
            })

            this.selectedWigsIds = validWigs
        },
        removeItem: function (wigId) {
            this.selectedItems = this.selectedItems.filter(
                (item) => item.wig.id !== wigId
            )

            this.selectedWigsIds = this.selectedWigsIds.filter(
                (wig) => wig.id !== wigId
            )
        },
        updateItemPrice: function (wigId, price) {
            const item = this.selectedItems.find(
                (item) => item.wig.id === wigId
            )
            if (item) {
                item.price =
                    price === '' || price === null || isNaN(price)
                        ? null
                        : Number(price)
            }
        },
        onWigsSelectionChange: function (wigs) {
            this.updateSelectedItems(wigs)
        },
        onCurrencyValidationError: function () {
            this.showWarning(this.$t('pages.shipments.multipleCurrenciesError'))
        },
        onWigsRefresh: async function () {
            if (this.hasSelectedSenders) {
                try {
                    const validSenders = this.selectedSenders.filter(
                        (sender) =>
                            sender !== null &&
                            sender !== '' &&
                            sender !== undefined
                    )
                    await this.fetchShipmentUsersWithWigs(validSenders)
                    this.syncSelectedItemsWithUpdatedWigs()
                } catch (error) {
                    console.error('Error refreshing wigs:', error)
                    this.showError(
                        error?.response?.data?.message ||
                            this.$t('pages.shipments.errorLoadingWigs')
                    )
                }
            }
        },
        syncSelectedItemsWithUpdatedWigs: function () {
            const allWigs = []

            if (this.availableWigs && this.availableWigs.length > 0) {
                allWigs.push(...this.availableWigs)
            }

            if (
                this.shipmentManagersWithWigs &&
                this.shipmentManagersWithWigs.length > 0
            ) {
                this.shipmentManagersWithWigs.forEach((manager) => {
                    if (manager.wigs && manager.wigs.length > 0) {
                        allWigs.push(...manager.wigs)
                    }
                })
            }

            this.selectedItems = this.selectedItems.map((item) => {
                const updatedWig = allWigs.find((wig) => wig.id === item.wig.id)
                if (updatedWig) {
                    return {
                        ...item,
                        wig: updatedWig,
                    }
                }
                return item
            })
        },
        onRecipientChange: function (recipientId) {
            this.selectedRecipientId = recipientId
        },
        submitForm: async function () {
            const allSendersFilled = this.selectedSenders.every(
                (sender) =>
                    sender !== null && sender !== '' && sender !== undefined
            )

            if (!allSendersFilled) {
                this.showError(
                    this.$t('pages.shipments.allSendersRequired') ||
                        'All sender fields must be filled'
                )
                return
            }

            if (!this.hasSelectedSenders) {
                this.showError(this.$t('pages.shipments.userRequired'))
                return
            }

            if (this.selectedItems.length === 0) {
                this.showError(this.$t('pages.shipments.atLeastOneWigRequired'))
                return
            }

            const itemsWithoutBarcode = this.selectedItems.filter(
                (item) => !item.wig?.barcode || item.wig.barcode.trim() === ''
            )
            if (itemsWithoutBarcode.length > 0) {
                this.showError(
                    this.$t('pages.shipments.allWigsMustHaveBarcode')
                )
                return
            }

            if (!this.recipientFirstName || !this.recipientFirstName.trim()) {
                this.showError(
                    this.$t('pages.shipments.recipientFirstNameRequired')
                )
                return
            }

            const priceError = this.getPriceValidationError(
                this.selectedItems
            )
            if (priceError) {
                this.showError(priceError)
                return
            }

            const currencies = this.selectedItems
                .map((item) => item.wig?.price_currency || 'ILS')
                .filter(
                    (currency, index, self) => self.indexOf(currency) === index
                )

            if (currencies.length > 1) {
                this.showWarning(
                    this.$t('pages.shipments.multipleCurrenciesError')
                )
                return
            }

            this.isSubmitting = true

            try {
                const validSenders = this.selectedSenders.filter(
                    (sender) =>
                        sender !== null && sender !== '' && sender !== undefined
                )

                const payload = {
                    sender_ids: validSenders,
                    items: this.selectedItems.map((item) => ({
                        wig_id: item.wig.id,
                        price: this.resolveItemPrice(item),
                    })),
                    recipient_first_name: this.recipientFirstName || '',
                }

                if (this.selectedRecipientId) {
                    payload.recipient_id = this.selectedRecipientId
                }

                const response = await this.createShipment(payload)

                const shipmentId =
                    response?.data?.shipment?.id ||
                    response?.data?.id ||
                    response?.shipment?.id ||
                    response?.id

                if (shipmentId) {
                    this.$router.push(`/shipments/${shipmentId}`)
                    return
                }

                this.$router.push('/shipments')
            } catch (error) {
                console.error('Error creating shipment:', error)

                this.showError(
                    error?.response?.data?.message ||
                        this.$t('pages.shipments.createError')
                )
            } finally {
                this.isSubmitting = false
            }
        },
        goBack: function () {
            const tabHasHistory = window.history.length > 1

            if (tabHasHistory) {
                return this.$router.back()
            }

            return this.$router.push('/shipments')
        },
    },
}
</script>

<style lang="scss" scoped>
.create-shipment {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @include tablet-up {
        height: calc(100dvh - 75px);
    }

    &__header {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        width: 100%;
    }

    &__back-btn {
        width: fit-content;
    }

    &__back-icon {
        @include for-hebrew {
            transform: scale(-1);
        }
    }

    &__title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    &__body {
        flex: 1;
        overflow: hidden;
        padding-top: 16px;

        @include tablet-up {
            padding: 16px 0;
        }
    }

    &__content {
        display: flex;
        gap: 8px;
        height: 100%;
        flex-direction: column;

        @include tablet-up {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr;
        }
    }
}
</style>
